import { DeleteOutlined, FileSyncOutlined, QuestionCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Modal, Space, Spin, Table, Tag } from 'antd';
import _ from 'lodash';
import React, { ReactElement, ReactNode } from 'react';
import { FaRegFilePdf } from 'react-icons/fa';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { currentAdministratorSelector } from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import RoleEnum from '~Api/Administrator/RoleEnum';
import IApplication from '~Api/Application/IApplication';
import IBorrower from '~Api/Application/IBorrower';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import ConditionTypeEnum from '~Api/Application/ConditionTypeEnum';
import ICondition from '~Api/Application/ICondition';
import IConditionDocument from '~Api/Application/IConditionDocument';
import BankruptcyCheck from '~Applications/Application/BankruptcyCheck';
import CompanyTradingHistory from '~Applications/Application/CompanyTradingHistory';
import CreditCheck from '~Applications/Application/CreditCheck';
import CreditMemo from '~Applications/Application/CreditMemo';
import InsuranceExpiryDate from '~Applications/Application/InsuranceExpiryDate';
import ValuationInspectionDate from '~Applications/Application/ValuationInspectionDate';
import { IGlobalState } from '~reducer';
import UploadConditionDocument from './UploadConditionDocument';
import UploadedConditionDocumentList from './UploadedConditionDocumentList';
import { IDictionary } from '~utilities/IDictionary';
import { ColumnType } from 'antd/lib/table';
import {
    applicationConditionDocumentsByConditionUuidSelector,
    applicationEntityConditionsSelector,
    applicationRfiItemsSelector,
} from '~Applications/selectors';
import {
    applicationConditionDeleteAction,
    applicationConditionDocumentsListAction,
    applicationConditionsListAction,
    applicationRfiItemDeleteAction,
    applicationRfiItemRequestedAction,
    applicationRfiItemSuppliedAction,
    applicationRfiItemsAddAction,
    applicationRfiItemsListAction,
} from '~Applications/actions';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import ConditionDocumentApprovalStatusEnum from '~Api/Application/ConditionDocumentApprovalStatusEnum';
import IRfiItem from '~Api/Application/IRfiItem';
import RfiWorkflowStatusEnum from '~Api/Application/RfiWorkflowStatusEnum';
import { aggregatorsListAction } from '~Aggregators/actions';
import { aggregatorsSelector } from '~Aggregators/selectors';
import IAggregator from '~Api/Aggregator/IAggregator';
import TitleSearch from '~Applications/Application/TitleSearch';
import { workflowStatusLabels } from '~Applications/workflowStatusLabels';
import ModalStateEnum from '~UI/ModalStateEnum';
import RequiredBeforeModal from './RequiredBeforeModal';
import Link from 'antd/lib/typography/Link';

const nextWorkflowStatuses: IDictionary<WorkflowStatusEnum> = {
    [WorkflowStatusEnum.Draft]: WorkflowStatusEnum.New,
    [WorkflowStatusEnum.New]: WorkflowStatusEnum.ConditionalApproval,
    [WorkflowStatusEnum.ConditionalApproval]: WorkflowStatusEnum.Underwriting,
    [WorkflowStatusEnum.Underwriting]: WorkflowStatusEnum.FormalApproval,
    [WorkflowStatusEnum.FormalApproval]: WorkflowStatusEnum.LegalDocuments,
    [WorkflowStatusEnum.LegalDocuments]: WorkflowStatusEnum.Settlement,
    [WorkflowStatusEnum.Settlement]: WorkflowStatusEnum.Warehoused,
};

enum WorkflowStatusNoneEnum {
    None = 'NONE',
}

type WorkflowStatusWithNoneEnum = WorkflowStatusEnum | WorkflowStatusNoneEnum;

const conditionUrls: IDictionary<string> = {
    [ConditionTypeEnum.AssetLiabilityStatement]: 'https://www.funding.com.au/assets-liabilities-statement/',
    [ConditionTypeEnum.BankSolicitorAccountantDetails]: 'https://www.funding.com.au/bank-solicitor-accountant-details/',
    [ConditionTypeEnum.BusinessPurposeDeclaration]: 'https://www.funding.com.au/business-purpose-declaration/',
    [ConditionTypeEnum.CompletedLoanApplication]: 'https://www.funding.com.au/loan-application/',
    [ConditionTypeEnum.ConsumerPurposeDeclaration]: 'https://www.funding.com.au/consumer-purpose-declaration/',
    [ConditionTypeEnum.LoanPurposeDeclaration]: 'https://www.funding.com.au/loan-purpose-declaration/',
    [ConditionTypeEnum.MonthlyBudget]: 'https://www.funding.com.au/monthly-budget/',
    [ConditionTypeEnum.PrivacyConsentForm]: 'https://www.funding.com.au/privacy-consent-form/',
    [ConditionTypeEnum.RecordOfInterview]: 'https://www.funding.com.au/record-of-interview/',
};

const conditionComponents: IDictionary<object> = {
    [ConditionTypeEnum.BankruptcyRegisterSearch]: BankruptcyCheck,
    [ConditionTypeEnum.BuildingReplacementInsurance]: InsuranceExpiryDate,
    [ConditionTypeEnum.CompanyInDepthTradingHistory]: CompanyTradingHistory,
    [ConditionTypeEnum.CreditFileSearchReport]: CreditCheck,
    [ConditionTypeEnum.CurrentTitleSearch]: TitleSearch,
    [ConditionTypeEnum.Memo]: CreditMemo,
    [ConditionTypeEnum.Valuation]: ValuationInspectionDate,
};

const statuses: WorkflowStatusEnum[] = [
    WorkflowStatusEnum.Draft,
    WorkflowStatusEnum.New,
    WorkflowStatusEnum.ConditionalApproval,
    WorkflowStatusEnum.Underwriting,
    WorkflowStatusEnum.FormalApproval,
    WorkflowStatusEnum.LegalDocuments,
    WorkflowStatusEnum.Settlement,
    WorkflowStatusEnum.Warehoused,
];

const rfiWorkflowStatusClassNames: IDictionary<string> = {
    [RfiWorkflowStatusEnum.Requested]: 'requested',
    [RfiWorkflowStatusEnum.Supplied]: 'supplied',
};

interface IConditionTypeDocument {
    conditionType?: ConditionTypeEnum;
    conditionUuid?: string;
    name: string;
    requiredWorkflowStatuses: WorkflowStatusEnum[];
    typedConditionDocuments?: IConditionDocument[];
    uid: string;
}

interface IProps {
    application: IApplication;
    applicationBorrower?: IBorrower;
    applicationProperty?: IApplicationProperty;
}

interface IPropsSelector {
    aggregators: IDictionary<IAggregator>;
    applicationEntityConditions: IDictionary<IDictionary<ICondition>>;
    conditionDocumentsByConditionUuid: IDictionary<IConditionDocument[]>;
    currentAdministrator: IAdministrator;
    rfiItems: IDictionary<IRfiItem>;
}

interface IPropsDispatch {
    aggregatorsList: () => void;
    applicationConditionDocumentsList: () => void;
    applicationConditionsList: () => void;
    conditionDelete: (applicationBorrowerUuid: string, applicationPropertyUuid: string, conditionUuid: string) => void;
    rfiItemDelete: (rfiItemUuid: string) => void;
    rfiItemRequested: (rfiItemUuid: string) => void;
    rfiItemsAdd: (applicationBorrowerUuid: string, applicationConditionUuid: string, applicationPropertyUuid: string, conditionType: ConditionTypeEnum) => void;
    rfiItemsList: () => void;
    rfiItemSupplied: (rfiItemUuid: string) => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

interface IState {
    requiredBeforeConditionUuid: string;
    requiredBeforeModalState: ModalStateEnum;
}

class ConditionDocumentsList extends React.Component<Props, IState> {
    public state: IState = {
        requiredBeforeConditionUuid: null,
        requiredBeforeModalState: ModalStateEnum.Off,
    };

    public componentDidMount(): void {
        const { aggregators, applicationEntityConditions, conditionDocumentsByConditionUuid, rfiItems } = this.props;

        if (!aggregators) {
            this.props.aggregatorsList();
        }

        if (!conditionDocumentsByConditionUuid) {
            this.props.applicationConditionDocumentsList();
        }

        if (!applicationEntityConditions) {
            this.props.applicationConditionsList();
        }

        if (!rfiItems) {
            this.props.rfiItemsList();
        }
    }

    public render(): JSX.Element {
        const {
            aggregators,
            application,
            applicationBorrower,
            applicationProperty,
            conditionDocumentsByConditionUuid,
            applicationEntityConditions,
            currentAdministrator,
            rfiItems,
        } = this.props;

        const {
            requiredBeforeConditionUuid,
            requiredBeforeModalState,
        } = this.state;

        if (!applicationEntityConditions || !aggregators || !conditionDocumentsByConditionUuid || !currentAdministrator || !rfiItems) {
            return (
                <Spin/>
            );
        }

        const entityUuid: string = (applicationBorrower && applicationBorrower.uuid) || (applicationProperty && applicationProperty.uuid) || application.uuid;
        const entityConditions: IDictionary<ICondition> = applicationEntityConditions[entityUuid] || {};
        const nextWorkflowStatus: WorkflowStatusEnum = nextWorkflowStatuses[application.workflowStatus] || null;

        const futureStatuses: WorkflowStatusEnum[] = [];
        const previousStatuses: WorkflowStatusEnum[] = [];
        let found: boolean = false;

        _.each(statuses, (status: WorkflowStatusEnum) => {
            if (status === nextWorkflowStatus) {
                found = true;
            }

            if (found) {
                futureStatuses.push(status);
            } else {
                previousStatuses.push(status);
            }
        });

        const sortedStatuses: WorkflowStatusWithNoneEnum[] = [
            ...futureStatuses,
            WorkflowStatusNoneEnum.None,
            ...previousStatuses,
        ];

        const sortOrderByWorkflowStatus: IDictionary<number> = {};
        _.each(sortedStatuses, (workflowStatus: WorkflowStatusWithNoneEnum, index: number) => {
            sortOrderByWorkflowStatus[workflowStatus] = index;
        });

        const conditionTypeDocuments: IConditionTypeDocument[] = _.map(entityConditions, (condition: ICondition) => {
            return {
                conditionType: condition.conditionType,
                conditionUuid: condition.uuid,
                name: condition.name,
                requiredWorkflowStatuses: condition.requiredWorkflowStatuses,
                typedConditionDocuments: conditionDocumentsByConditionUuid[condition.uuid] ? conditionDocumentsByConditionUuid[condition.uuid] : [],
                uid: condition.uuid,
            };
        });

        const columns: ColumnType<IConditionTypeDocument>[] = [
            {
                dataIndex: 'typedConditionDocuments',
                render: (typedConditionDocuments: IConditionDocument[], conditionTypeDocument: IConditionTypeDocument): ReactElement => {
                    if (_.some(typedConditionDocuments, (typedConditionDocument: IConditionDocument) => [
                        ConditionDocumentApprovalStatusEnum.Legacy,
                        ConditionDocumentApprovalStatusEnum.Approved,
                    ].includes(typedConditionDocument.approvalStatus))) {
                        return;
                    }

                    if (conditionTypeDocument.requiredWorkflowStatuses.includes(nextWorkflowStatus)) {
                        return <WarningOutlined/>;
                    }

                    return <QuestionCircleOutlined/>;
                },
                title: '',
                width: 50,
            },
            {
                render: (conditionTypeDocument: IConditionTypeDocument): ReactElement => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const ConditionComponent: any = conditionComponents[conditionTypeDocument.conditionType];
                    const conditionComponentBlock: JSX.Element = ConditionComponent && (
                        <ConditionComponent
                            applicationUuid={application.uuid}
                            applicationBorrowerUuid={applicationBorrower && applicationBorrower.uuid}
                            applicationPropertyUuid={applicationProperty && applicationProperty.uuid}
                        />
                    );

                    return (
                        <Space>
                            <span>{conditionTypeDocument.name}</span>
                            {conditionUrls[conditionTypeDocument.conditionType] && <a href={conditionUrls[conditionTypeDocument.conditionType]} target='_blank' rel='noreferrer'><FaRegFilePdf/></a>}
                            {conditionComponentBlock}
                        </Space>
                    );
                },
                title: 'Condition',
            },
            {
                render: (conditionTypeDocument: IConditionTypeDocument): ReactElement => (
                    <UploadedConditionDocumentList
                        application={application}
                        applicationBorrower={applicationBorrower}
                        applicationProperty={applicationProperty}
                        conditionUuid={conditionTypeDocument.conditionUuid}
                        conditionType={conditionTypeDocument.conditionType}
                    />
                ),
                title: 'Documents',
                width: 460,
            },
            {
                render: (conditionTypeDocument: IConditionTypeDocument): ReactElement => {
                    const rfiItem: IRfiItem = _.find(rfiItems, { applicationConditionUuid: conditionTypeDocument.conditionUuid });

                    const isOwnerOrSenior: boolean = application.administratorUuid === currentAdministrator.uuid || RoleEnum.SeniorCreditManager === currentAdministrator.role;

                    const rfiMenu: MenuProps = {
                        className: 'rfi-item-dropdown',
                        items: [
                            ...(!rfiItem ? [
                                {
                                    key: 'request',
                                    label: 'Request',
                                    onClick: () => this.props.rfiItemsAdd(
                                        applicationBorrower && applicationBorrower.uuid,
                                        conditionTypeDocument.conditionUuid,
                                        applicationProperty && applicationProperty.uuid,
                                        conditionTypeDocument.conditionType,
                                    ),
                                },
                            ] : []),
                            ...(rfiItem && rfiItem.workflowStatus !== RfiWorkflowStatusEnum.Requested ? [
                                {
                                    key: 'requested',
                                    label: 'Requested',
                                    onClick: () => this.props.rfiItemRequested(rfiItem.uuid),
                                },
                            ] : []),
                            ...(isOwnerOrSenior && rfiItem && rfiItem.workflowStatus !== RfiWorkflowStatusEnum.Supplied ? [
                                {
                                    key: 'supplied',
                                    label: 'Supplied',
                                    onClick: () => this.props.rfiItemSupplied(rfiItem.uuid),
                                },
                            ] : []),
                            ...(isOwnerOrSenior && rfiItem ? [
                                {
                                    danger: true,
                                    key: 'cancel-request',
                                    label: 'Cancel Request',
                                    onClick: () => this.props.rfiItemDelete(rfiItem.uuid),
                                },
                            ] : []),
                        ],
                    };

                    const rfiBlock: JSX.Element = (isOwnerOrSenior || RoleEnum.CreditManager === currentAdministrator.role) && (
                        <Dropdown menu={rfiMenu}>
                            <Button className={`rfi-item ${rfiItem && rfiWorkflowStatusClassNames[rfiItem.workflowStatus]}`} type='link'>
                                <FileSyncOutlined/>
                            </Button>
                        </Dropdown>
                    );

                    const onClickDeleteCustomCondition: () => void = (): void => {
                        Modal.confirm({
                            content: 'Are you sure you want to delete this condition?',
                            okText: 'Delete',
                            okType: 'danger',
                            onOk: () => {
                                const applicationPropertyUuid: string = applicationProperty ? applicationProperty.uuid : null;
                                const applicationBorrowerUuid: string = applicationBorrower ? applicationBorrower.uuid : null;
                                this.props.conditionDelete(applicationBorrowerUuid, applicationPropertyUuid, conditionTypeDocument.conditionUuid);
                            },
                            title: 'Delete Condition',
                        });
                    };

                    const deleteCustomConditionBlock: ReactElement = !conditionTypeDocument.conditionType && (
                        <Button
                            danger={true}
                            type='link'
                            onClick={onClickDeleteCustomCondition}
                        >
                            <DeleteOutlined/>
                        </Button>
                    );

                    return (
                        <Space>
                            <UploadConditionDocument
                                application={application}
                                applicationBorrower={applicationBorrower}
                                applicationProperty={applicationProperty}
                                conditionUuid={conditionTypeDocument.conditionUuid}
                                conditionType={conditionTypeDocument.conditionType}
                            />
                            {rfiBlock}
                            {deleteCustomConditionBlock}
                        </Space>
                    );
                },
                title: 'Actions',
                width: 132,
            },
            {
                defaultSortOrder: 'ascend',
                filters: _.map(sortedStatuses, (workflowStatus: WorkflowStatusWithNoneEnum) => ({
                    text: workflowStatus !== WorkflowStatusNoneEnum.None ? workflowStatusLabels[workflowStatus] : 'None',
                    value: workflowStatus,
                })),
                onFilter: (value: boolean | number | string, conditionTypeDocument: IConditionTypeDocument) => conditionTypeDocument.requiredWorkflowStatuses.includes(value as WorkflowStatusEnum),
                render: (conditionTypeDocument: IConditionTypeDocument): ReactNode => {
                    const condition: ICondition = applicationEntityConditions[entityUuid][conditionTypeDocument.conditionUuid];

                    if (condition.conditionType) {
                        return conditionTypeDocument.requiredWorkflowStatuses.length > 0 && (
                            <Tag className='required-before-tag disabled'>
                                { workflowStatusLabels[conditionTypeDocument.requiredWorkflowStatuses[0]] }
                            </Tag>
                        );
                    }

                    const onClickRequiredBefore: () => void = () => {
                        this.setState({
                            requiredBeforeConditionUuid: condition.uuid,
                            requiredBeforeModalState: ModalStateEnum.Open,
                        });
                    };
                    const onCancelRequiredBefore: () => void = (): void => {
                        this.setState({
                            requiredBeforeModalState: ModalStateEnum.Closed,
                        });
                    };

                    const requiredBeforeModalBlock: ReactElement = condition.uuid === requiredBeforeConditionUuid && (
                        <RequiredBeforeModal
                            condition={condition}
                            isOpen={requiredBeforeModalState === ModalStateEnum.Open}
                            onCancel={onCancelRequiredBefore}
                        />
                    );

                    return (
                        <>
                            <Link className='required-before-tag' onClick={onClickRequiredBefore}>
                                <Tag>
                                    { workflowStatusLabels[conditionTypeDocument.requiredWorkflowStatuses[0]] }
                                </Tag>
                            </Link>
                            {requiredBeforeModalBlock}
                        </>
                    );
                },
                sorter: (a: IConditionTypeDocument, b: IConditionTypeDocument): number => {
                    const aSortOrder: number = sortOrderByWorkflowStatus[(
                        a.requiredWorkflowStatuses.length > 0
                        ? a.requiredWorkflowStatuses[0]
                        : WorkflowStatusNoneEnum.None
                    )];

                    const bSortOrder: number = sortOrderByWorkflowStatus[(
                        b.requiredWorkflowStatuses.length > 0
                        ? b.requiredWorkflowStatuses[0]
                        : WorkflowStatusNoneEnum.None
                    )];

                    if (aSortOrder !== bSortOrder) {
                        return aSortOrder - bSortOrder;
                    }

                    return a.name.localeCompare(b.name);
                },
                title: 'Required Before',
                width: 184,
            },
        ];

        return (
            <Table
                columns={columns}
                dataSource={conditionTypeDocuments}
                pagination={false}
                rowKey='uid'
            />
        );
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        aggregators: aggregatorsSelector(state),
        applicationEntityConditions: applicationEntityConditionsSelector(state, ownProps.application.uuid),
        conditionDocumentsByConditionUuid: applicationConditionDocumentsByConditionUuidSelector(state, ownProps.application.uuid),
        currentAdministrator: currentAdministratorSelector(state),
        rfiItems: applicationRfiItemsSelector(state, ownProps.application.uuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        aggregatorsList: () => dispatch(aggregatorsListAction()),
        applicationConditionDocumentsList: () => dispatch(applicationConditionDocumentsListAction(ownProps.application.uuid)),
        applicationConditionsList: () => dispatch(applicationConditionsListAction(ownProps.application.uuid)),
        conditionDelete: (applicationBorrowerUuid: string, applicationPropertyUuid: string, conditionUuid: string) => dispatch(applicationConditionDeleteAction(ownProps.application.uuid, applicationBorrowerUuid, applicationPropertyUuid, conditionUuid)),
        rfiItemDelete: (rfiItemUuid: string) => dispatch(applicationRfiItemDeleteAction(ownProps.application.uuid, rfiItemUuid)),
        rfiItemRequested: (rfiItemUuid: string) => dispatch(applicationRfiItemRequestedAction(rfiItemUuid)),
        rfiItemSupplied: (rfiItemUuid: string) => dispatch(applicationRfiItemSuppliedAction(rfiItemUuid)),
        rfiItemsAdd: (applicationBorrowerUuid: string, applicationConditionUuid: string, applicationPropertyUuid: string, conditionType: ConditionTypeEnum) => dispatch(applicationRfiItemsAddAction(ownProps.application.uuid, applicationBorrowerUuid, applicationConditionUuid, applicationPropertyUuid, conditionType)),
        rfiItemsList: () => dispatch(applicationRfiItemsListAction(ownProps.application.uuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ConditionDocumentsList);
